import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MapPin } from "lucide-react";
import { SectionHeading } from "@/components/Reveal";
import { FILTERS, PRODUCTIONS } from "@/data/content";

export default function Productions() {
  const [active, setActive] = useState("All");
  const visible = active === "All" ? PRODUCTIONS : PRODUCTIONS.filter((p) => p.category === active);

  return (
    <section id="productions" data-testid="productions-section" className="py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <SectionHeading
          label="Selected Productions"
          title="Shows that"
          accent="had to be perfect"
          description="A snapshot of the stages I've engineered — arena concerts, festivals, exhibitions and conferences across the UAE and beyond."
          testId="productions-heading"
        />

        <div className="mb-12 flex flex-wrap gap-2" role="tablist" aria-label="Filter productions" data-testid="productions-filters">
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              role="tab"
              aria-selected={active === f}
              data-testid={`filter-${f.toLowerCase()}`}
              onClick={() => setActive(f)}
              className={`rounded-full px-5 py-2.5 text-xs font-extrabold uppercase tracking-[0.15em] transition-[background-color,color,border-color] duration-300 ${
                active === f
                  ? "bg-electric text-[#050505]"
                  : "border border-white/10 bg-white/[0.03] text-neutral-400 hover:border-electric/50 hover:text-neutral-100"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <motion.div layout className="grid gap-8 md:grid-cols-2 lg:grid-cols-3" data-testid="productions-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.title}
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`production-card-${p.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group overflow-hidden rounded-2xl border border-white/10 bg-[#0A0A0A] transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-electric/50"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.title} — ${p.venue}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" aria-hidden="true" />
                  <span className="absolute left-4 top-4 rounded-full bg-black/70 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-electric backdrop-blur-md">
                    {p.category}
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-semibold uppercase tracking-tight text-neutral-100">{p.title}</h3>
                  <p className="mt-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
                    <MapPin size={13} className="text-electric" aria-hidden="true" /> {p.venue}
                  </p>
                  <div className="mt-4 space-y-1 border-t border-white/5 pt-4 text-xs uppercase tracking-[0.1em]">
                    <p className="text-neutral-400"><span className="text-neutral-600">Role — </span>{p.role}</p>
                    <p className="text-neutral-400"><span className="text-neutral-600">Production — </span>{p.production}</p>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-neutral-400">{p.description}</p>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <p className="mt-12 text-sm text-neutral-500" data-testid="productions-more-note">
          Plus 12+ more productions across the region — including GITEX Global, ADIPEC, Arab Media Forum,
          Tomorrowland Amare Stage and Dimitri Vegas & Like Mike.{" "}
          <a href="#experience" data-testid="productions-full-list-link" className="font-semibold text-electric underline-offset-4 hover:underline">
            See the full list
          </a>
        </p>
      </div>
    </section>
  );
}
